import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Collapse, Button, Stack, IconButton, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { EnhancedDataGridWrapper } from '../Common';
import SearchCondition from '../Common/SearchCondition';
import {
  getOrderHeaders, getOrderDetails, getVendors, getProducts, getPaymentMethods,
  upsertOrderHeaders, upsertOrderDetails, deleteOrderHeader, deleteOrderDetail
} from '../../api/orderApi';
import Message from '../../utils/message/Message';
import { format, parse } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import koLocale from 'date-fns/locale/ko';
import UseSystemStatusManager from "../../hook/UseSystemStatusManager";
import { useExcelUpload, useExcelTemplateInfo } from '../../utils/excelUploadApi';
import { downloadExcelTemplate } from '../../utils/excelImportUtils';

const OrderRegistration = () => {
  const { userGroup } = UseSystemStatusManager()
  // 상태 관리
  const [searchParams, setSearchParams] = useState({
    orderNo: null,
    fromDate: null,
    toDate: null,
    customerId: null,
    materialId: null
  });

  // 주문번호 입력값을 별도로 관리
  const [orderNoInput, setOrderNoInput] = useState('');

  const [headerRows, setHeaderRows] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editedHeaderRows, setEditedHeaderRows] = useState(new Set());
  const [editedDetailRows, setEditedDetailRows] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);

  // 엑셀 업로드 관련 훅
  const { uploadExcel, loading: uploadLoading } = useExcelUpload();
  const { templateInfo } = useExcelTemplateInfo('ORDER_HEADER');

  // 초기 데이터 로딩
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [vendorList, productList, paymentList, headers] = await Promise.all([
        getVendors(),
        getProducts(),
        getPaymentMethods(),
        getOrderHeaders({})
      ]);
      
      setVendors(vendorList || []);
      setProducts(productList || []);
      setPaymentMethods(paymentList || []);
      setHeaderRows(headers || []);
    } catch (error) {
      Message.showError(error);
    } finally {
      setLoading(false);
    }
  };

  // 거래처 목록 재조회 (신규 거래처 등록 후 즉시 선택 가능하도록)
  const loadVendors = async () => {
    try {
      const vendorList = await getVendors();
      setVendors(vendorList || []);
    } catch (error) {
      Message.showError(error);
    }
  };

  // 검색 조건 변경 시 헤더 데이터 조회 (주문번호 제외)
  const handleSearch = async (newParams, isButtonClick = false) => {
    try {
      setLoading(true);
      // 빈 문자열을 null로 변환
      const cleanedParams = Object.fromEntries(
        Object.entries(newParams).map(([key, value]) => [
          key,
          value === '' ? null : value
        ])
      );

      // 버튼 클릭이 아닌 경우, 주문번호는 이전 값 유지
      const finalParams = isButtonClick 
        ? { ...cleanedParams, orderNo: orderNoInput || null }
        : { ...cleanedParams, orderNo: searchParams.orderNo };
      
      setSearchParams(finalParams);
      const headers = await getOrderHeaders(finalParams);
      setHeaderRows(headers || []);
      // 검색 시 선택된 헤더 초기화
      setSelectedHeader(null);
      setDetailRows([]);
    } catch (error) {
      Message.showError(error);
    } finally {
      setLoading(false);
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    const initialParams = {
      orderNo: null,
      fromDate: null,
      toDate: null,
      customerId: null,
      materialId: null
    };
    setOrderNoInput('');
    handleSearch(initialParams);
  };

  // 헤더 선택 시 상세 데이터 조회
  useEffect(() => {
    const loadDetails = async () => {
      if (!selectedHeader?.id) {
        setDetailRows([]);
        return;
      }
      
      try {
        setLoading(true);
        const details = await getOrderDetails(selectedHeader.orderNo);
        setDetailRows(details || []);
      } catch (error) {
        Message.showError(error);
      } finally {
        setLoading(false);
      }
    };
    
    loadDetails();
  }, [selectedHeader]);

  // 날짜 포맷 변환 함수
  const formatDate = (date) => {
    if (!date) return '';  // null/undefined 처리
    return format(new Date(date), 'yyyy-MM-dd');
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    return parse(dateStr, 'yyyy-MM-dd', new Date());
  };

  // 날짜 선택 컴포넌트
  const DatePickerCell = ({ value, field, id, api }) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
        <DatePicker
          value={parseDate(value)}
          onChange={(newValue) => {
            api.setEditCellValue({ id, field, value: formatDate(newValue) });
          }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              variant: "outlined"
            }
          }}
          format="yyyy-MM-dd"
        />
      </LocalizationProvider>
    );
  };

  // 행 수정 처리 - 헤더
  const processHeaderRowUpdate = (newRow, oldRow) => {
    const updatedRow = { ...newRow };
    setHeaderRows(prev => prev.map(row => 
      row.orderNo === oldRow.orderNo ? updatedRow : row
    ));
    // 수정된 행 추적
    if (updatedRow.id) {
      setEditedHeaderRows(prev => new Set(prev).add(updatedRow.orderNo));
    }
    return updatedRow;
  };

  // 행 수정 처리 - 상세
  const processDetailRowUpdate = (newRow, oldRow) => {
    const updatedRow = { ...newRow };
    setDetailRows(prev => prev.map(row => 
      row.orderSubNo === oldRow.orderSubNo ? updatedRow : row
    ));
    // 수정된 행 추적
    if (updatedRow.id !== null) {
      setEditedDetailRows(prev => new Set(prev).add(updatedRow.orderSubNo));
    }
    return updatedRow;
  };

  // 저장할 헤더 행들 필터링
  const getHeaderRowsToSave = () => {
    return headerRows.filter(row => {
      return !row.id || editedHeaderRows.has(row.orderNo);
    });
  };

  // 저장할 상세 행들 필터링
  const getDetailRowsToSave = () => {
    return detailRows.filter(row => {
      return row.id === null || editedDetailRows.has(row.orderSubNo);
    });
  };

  // 헤더 컬럼 정의 - 동적으로 생성하여 초기 데이터 로딩 후 업데이트
  const headerColumns = React.useMemo(() => [
    { 
      field: 'orderNo', 
      headerName: '주문번호', 
      width: 120,
      renderCell: (params) => {
        // 신규 행이거나 orderNo가 임시 ID인 경우 "자동입력" 표시
        if (!params.row.id || params.value?.startsWith('temp_header_')) {
          return '자동입력';
        }
        return params.value || '';
      }
    },
    {
      field: 'orderDate',
      headerName: '주문일자',
      width: 120,
      editable: true,
      required: true,
      renderCell: (params) => renderRequiredCell(params, 'orderDate'),
      renderEditCell: (params) => <DatePickerCell {...params} />,
      valueFormatter: (params) => params?.value ? formatDate(params.value) : ''
    },
    {
      field: 'customerId',
      headerName: '고객사',
      width: 150,
      editable: true,
      required: true,
      type: 'singleSelect',
      valueOptions: vendors.map(v => ({
        value: v.vendorId,
        label: v.vendorName,
      })),
      renderCell: (params) => renderRequiredCell(params, 'customerId')
    },
      {
        field: 'orderer',
        headerName: '주문자',
        width: 120,
        editable: true,
        type: 'singleSelect',
        valueOptions: userGroup.map(u => ({
          value: u.loginId,
          label: u.userName
        })),
        renderCell: (params) => renderCell(params, 'orderer')
      },
      {
        field: 'flagVatAmount',
        headerName: '부가세여부',
        width: 100,
        editable: true,
        type: 'boolean',
        renderCell: (params) => params.value ? '예' : '아니오'
      },
      {
        field: 'orderQuantity',
        headerName: '주문수량',
        width: 120,
        editable: false,
        type: 'number',
        renderCell: (params) => params.value || '자동계산'
      },
      {
        field: 'totalAmount',
        headerName: '총금액',
        width: 120,
        type: 'number',
        editable: false,
        renderCell: (params) => params.value?.toLocaleString() || '자동계산'
      },
      {
        field: 'vatAmount',
        headerName: '부가세',
        width: 120,
        type: 'number',
        editable: false,
        renderCell: (params) => params.value?.toLocaleString() || '자동계산'
      },
      {
        field: 'finalAmount',
        headerName: '최종금액',
        width: 120,
        type: 'number',
        editable: false,
        renderCell: (params) => params.value?.toLocaleString() || '자동계산'
      },
      {
        field: 'deliveryDate',
        headerName: '납기일',
        width: 120,
        editable: true,
        renderCell: (params) => renderCell(params, 'deliveryDate'),
        renderEditCell: (params) => <DatePickerCell {...params} />,
        valueFormatter: (params) => params?.value ? formatDate(params.value) : ''
      },
      {
        field: 'paymentMethod',
        headerName: '결제방법',
        width: 120,
        editable: true,
        type: 'singleSelect',
        valueOptions: paymentMethods.map(m => ({
          value: m.codeId,
          label: m.codeName
        })),
        renderCell: (params) => renderCell(params, 'paymentMethod')
      },
      {
        field: 'deliveryAddr',
        headerName: '배송주소',
        width: 200,
        editable: true,
        renderCell: (params) => renderCell(params, 'deliveryAddr')
      }
  ], [vendors, paymentMethods, userGroup]);

  // 상세 컬럼 정의 - 동적으로 생성하여 초기 데이터 로딩 후 업데이트
  const detailColumns = React.useMemo(() => [
    {
      field: 'orderSubNo',
      headerName: '상세번호',
      width: 120,
      editable: false,
      required: true,
      renderCell: (params) => {
        // 신규 행이거나 orderSubNo가 임시 ID인 경우 "자동입력" 표시
        if (!params.row.id || params.value?.startsWith('temp_detail_')) {
          return '자동입력';
        }
        return params.value || '자동입력';
      }
    },
    {
      field: 'systemMaterialId',
      headerName: '품목ID',
      width: 150,
      editable: true,
      required: true,
      type: 'singleSelect',
      valueOptions: products.map(p => ({
        value: p.systemMaterialId,
        label: `${p.materialName} (${p.materialStandard})`
      })),
      renderCell: (params) => renderRequiredCell(params, 'systemMaterialId')
    },
    {
      field: 'materialName',
      headerName: '품목명',
      width: 200,
      editable: false,
      required: true,
      renderCell: (params) => params.value || '자동입력'
    },
    {
      field: 'materialStandard',
      headerName: '규격',
      width: 120,
      editable: false,
      required: true,
      renderCell: (params) => params.value || '자동입력'
    },
    {
      field: 'unit',
      headerName: '단위',
      width: 80,
      editable: false,
      required: true,
      renderCell: (params) => params.value || '자동입력'
    },
    { 
      field: 'quantity', 
      headerName: '수량', 
      width: 100,
      type: 'number',
      editable: true,
      required: true,
      renderCell: (params) => renderRequiredCell(params, 'quantity')
    },
    { 
      field: 'unitPrice', 
      headerName: '단가', 
      width: 120,
      type: 'number',
      editable: true,
      required: true,
      renderCell: (params) => renderRequiredCell(params, 'unitPrice')
    },
    {
      field: 'supplyPrice',
      headerName: '공급가',
      width: 120,
      type: 'number',
      editable: false,
      renderCell: (params) => params.value?.toLocaleString() || '자동계산'
    },
    {
      field: 'vatPrice',
      headerName: '부가세',
      width: 120,
      type: 'number',
      editable: false,
      renderCell: (params) => params.value?.toLocaleString() || '자동계산'
    },
    { 
      field: 'totalPrice',
      headerName: '합계금액',
      width: 120,
      type: 'number',
      editable: false,
      renderCell: (params) => params.value?.toLocaleString() || '자동계산'
    },
    { 
      field: 'deliveryDate', 
      headerName: '납기일자', 
      width: 130,
      editable: true,
      renderEditCell: DatePickerCell
    },
    { 
      field: 'remark', 
      headerName: '비고', 
      width: 200,
      editable: true
    }
  ], [products]);

  const EditButton = ({ params }) => {
    const handleClick = (event) => {
      event.stopPropagation();
      setIsEditMode(true);
    };

    return (
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    );
  };

  // 셀 편집 가능 여부 - 헤더
  const isHeaderCellEditable = (params) => {
    if (!params || !params.row) return false;
    return isEditMode || !params.row.id;
  };

  // 셀 편집 가능 여부 - 상세
  const isDetailCellEditable = (params) => {
    if (!params || !params.row) return false;
    return true;
  };

  // 셀 렌더링
  const renderCell = (params, field) => {
    if (!params) return <div></div>;
    
    let displayValue = params.value || '';
    
    // 필드별 값 매핑
    if (field === 'orderer') {
      const user = userGroup.find(u => u.loginId === params.value);
      displayValue = user?.userName || params.value || '';
    } else if (field === 'customerId') {
      const vendor = vendors.find(v => v.vendorId === params.value);
      displayValue = vendor?.vendorName || params.value || '';
    } else if (field === 'paymentMethod') {
      const method = paymentMethods.find(m => m.codeId === params.value);
      displayValue = method?.codeName || params.value || '';
    }
    
    return (
      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {displayValue}
      </div>
    );
  };

  // 필수 필드 셀 렌더링
  const renderRequiredCell = (params, field) => {
    if (!params || !params.row) {
      return (
        <div style={{ 
          color: '#f44336',
          whiteSpace: 'nowrap', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis' 
        }}>
          필수 입력
        </div>
      );
    }

    const value = params.value;
    let displayValue = params.formattedValue || value; // formattedValue 우선 사용
    
    // 필드별 값 매핑 (필수 필드의 경우)
    if (!params.formattedValue && value) {
      if (field === 'customerId') {
        const vendor = vendors.find(v => v.vendorId === value);
        displayValue = vendor?.vendorName || value;
      }
    }
    
    const isEmpty = !value || value === '';
    const isNewRow = !params.row.id;
    
    return (
      <div style={{ 
        color: isEmpty && isNewRow ? '#f44336' : 'inherit',
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis' 
      }}>
        {isEmpty && isNewRow ? '필수 입력' : displayValue}
      </div>
    );
  };

  // 헤더 행 클릭 핸들러
  const handleHeaderRowClick = (params) => {
    if (params.row.id) {
      setSelectedHeader(params.row);
    } else {
      setSelectedHeader(null);
      setDetailRows([]);
    }
  };

  // 헤더 행 더블클릭 핸들러 (편집모드)
  const handleHeaderRowDoubleClick = (params) => {
    if (params.row.id) {
      setIsEditMode(true);
    }
  };

  // 상세 데이터 로딩
  const loadDetails = async (orderNo) => {
    try {
      setLoading(true);
      const details = await getOrderDetails(orderNo);
      setDetailRows(details || []);
    } catch (error) {
      Message.showError(error);
    } finally {
      setLoading(false);
    }
  };

  // 헤더 추가
  const handleAddHeader = () => {
    // 거래처 목록 재조회 (신규 거래처 즉시 선택 가능하도록)
    loadVendors();

    // 임시 ID 생성 (신규 행 구분용)
    const tempId = `temp_header_${Date.now()}_${Math.random()}`;
    
    // 백엔드에서 자동 생성하므로 빈 객체로 새 행 생성
    const newHeader = {
      id: null, // 신규 행 표시 (실제 DB ID)
      tempId, // Data Grid용 임시 unique ID
      orderNo: tempId, // orderNo도 임시 ID로 설정 (getRowId에서 사용)
      site: null,
      compCd: null,
      orderDate: new Date().toISOString().split('T')[0], // 기본값: 오늘 날짜
      customerId: null,
      orderer: null,
      flagVatAmount: false,
      orderQuantity: null,
      totalAmount: null,
      vatAmount: null,
      finalAmount: null,
      deliveryDate: null,
      paymentMethod: null,
      deliveryAddr: null,
      orderStatus: null,
      createUser: null,
      createDate: null,
      remark: null,
      flagActive: true
    };
    
    setHeaderRows(prev => [newHeader, ...prev]);
    setSelectedHeader(newHeader);
    setDetailRows([]);
  };

  // 상세 추가
  const handleAddDetail = () => {
    if (!selectedHeader) {
      Message.showError('먼저 주문 헤더를 선택해주세요.');
      return;
    }

    // 임시 ID 생성 (신규 행 구분용)
    const tempId = `temp_detail_${Date.now()}_${Math.random()}`;

    // 백엔드에서 자동 생성하므로 빈 객체로 새 행 생성
    const newDetail = {
      id: null, // 신규 행 표시 (실제 DB ID)
      tempId, // Data Grid용 임시 unique ID  
      orderNo: selectedHeader.orderNo,
      orderSubNo: tempId, // orderSubNo도 임시 ID로 설정 (getRowId에서 사용)
      systemMaterialId: null,
      materialName: null,
      materialStandard: null,
      unit: null,
      deliveryDate: null,
      quantity: null,
      unitPrice: null,
      supplyPrice: null,
      vatPrice: null,
      totalPrice: null,
      remark: null,
      flagActive: true
    };
    
    setDetailRows(prev => [...prev, newDetail]);
  };

  // 헤더 삭제
  const handleDeleteHeader = async (id) => {
    if (!id) {
      Message.showError('삭제할 주문을 선택해주세요.');
      return;
    }

    try {
      const headerToDelete = headerRows.find(row => row.id === id);
      if (!headerToDelete) return;

      await deleteOrderHeader(headerToDelete.id);
      
      setHeaderRows(prev => prev.filter(row => row.id !== id));
      setSelectedHeader(null);
      setDetailRows([]);
      
      Message.showSuccess('주문이 삭제되었습니다.');
    } catch (error) {
      Message.showError(error);
    }
  };

  // 상세 삭제
  const handleDeleteDetail = async () => {
    if (!selectedDetail) {
      Message.showError('삭제할 상세를 선택해주세요.');
      return;
    }

    try {
      if (selectedDetail.id) {
        await deleteOrderDetail(selectedDetail.id);
      }
      
      // 저장 후 헤더 데이터 새로고침 (집계 필드 업데이트를 위해)
      const updatedHeaders = await getOrderHeaders(searchParams);
      setHeaderRows(updatedHeaders || []);
      
      // 현재 선택된 헤더 업데이트
      if (selectedHeader) {
        const updatedHeader = updatedHeaders?.find(h => h.orderNo === selectedHeader.orderNo);
        if (updatedHeader) {
          setSelectedHeader(updatedHeader);
        }
      }
      
      setDetailRows(prev => prev.filter(row => row.orderSubNo !== selectedDetail.orderSubNo));
      setSelectedDetail(null);
      
      //Message.showSuccess('주문 상세가 삭제되었습니다.');
    } catch (error) {
      Message.showError(error);
    }
  };

  // 상세 행 클릭 핸들러
  const handleDetailRowClick = (params) => {
    setSelectedDetail(params.row);
  };

  // 헤더 저장
  const handleSaveHeader = async () => {
    try {
      const rowsToSave = getHeaderRowsToSave();
      if (rowsToSave.length === 0) {
        Message.showWarning('저장할 데이터가 없습니다.');
        return;
      }

      // 필수 필드 검증
      for (const row of rowsToSave) {
        if (!row.customerId) {
          Message.showError('고객사는 필수 입력 항목입니다.');
          return;
        }
        if (!row.orderDate) {
          Message.showError('주문일자는 필수 입력 항목입니다.');
          return;
        }
      }

      // OrderHeaderRequest에 맞게 필드 필터링 (tempId 제외, null 값들 제거)
      const filteredRowsToSave = rowsToSave.map(row => {
        const filtered = {
          orderDate: row.orderDate,
          customerId: row.customerId,
          flagVatAmount: row.flagVatAmount || false
        };
        
        // 기존 행인 경우 id와 orderNo 포함
        if (row.id && !row.orderNo?.startsWith('temp_header_')) {
          filtered.id = row.id;
          filtered.orderNo = row.orderNo;
        }
        
        // null이 아닌 값들만 추가
        if (row.site) filtered.site = row.site;
        if (row.compCd) filtered.compCd = row.compCd;
        if (row.orderer) filtered.orderer = row.orderer;
        if (row.deliveryDate) filtered.deliveryDate = row.deliveryDate;
        if (row.paymentMethod) filtered.paymentMethod = row.paymentMethod;
        if (row.deliveryAddr) filtered.deliveryAddr = row.deliveryAddr;
        if (row.remark) filtered.remark = row.remark;
        
        return filtered;
      });

      console.log('Sending header data:', filteredRowsToSave);
      await upsertOrderHeaders(filteredRowsToSave);
      
      // 저장 후 데이터 새로고침
      const updatedHeaders = await getOrderHeaders(searchParams);
      setHeaderRows(updatedHeaders || []);
      setEditedHeaderRows(new Set());
      setIsEditMode(false);
      
      Message.showSuccess('헤더 정보가 저장되었습니다.');
    } catch (error) {
      Message.showError(error);
    }
  };

  // 상세 저장
  const handleSaveDetail = async () => {
    try {
      const rowsToSave = getDetailRowsToSave();
      if (rowsToSave.length === 0) {
        Message.showWarning('저장할 데이터가 없습니다.');
        return;
      }

      // 필수 필드 검증
      for (const row of rowsToSave) {
        if (!row.systemMaterialId) {
          Message.showError('제품은 필수 입력 항목입니다.');
          return;
        }
        if (!row.quantity || row.quantity <= 0) {
          Message.showError('수량은 0보다 큰 값을 입력해주세요.');
          return;
        }
        if (!row.unitPrice || row.unitPrice <= 0) {
          Message.showError('단가는 0보다 큰 값을 입력해주세요.');
          return;
        }
      }

      // OrderDetailRequest에 맞게 필드 필터링 (tempId 제외, 신규 행은 orderSubNo도 제외)
      const filteredRowsToSave = rowsToSave.map(row => {
        // 헤더의 orderNo가 임시 ID인 경우 실제 orderNo를 사용해야 하므로 undefined로 설정
        let actualOrderNo = row.orderNo;
        if (selectedHeader?.orderNo?.startsWith('temp_header_')) {
          actualOrderNo = undefined; // 백엔드에서 자동 매핑
        }
        
        return {
          id: row.id,
          site: row.site,
          compCd: row.compCd,
          orderNo: actualOrderNo,
          orderSubNo: row.orderSubNo?.startsWith('temp_detail_') ? undefined : row.orderSubNo,
          systemMaterialId: row.systemMaterialId,
          deliveryDate: row.deliveryDate,
          quantity: row.quantity,
          unitPrice: row.unitPrice,
          supplyPrice: row.supplyPrice,
          discountedAmount: row.discountedAmount,
          remark: row.remark,
          flagVatAmount: selectedHeader?.flagVatAmount || false
        };
      });

      await upsertOrderDetails(filteredRowsToSave);
      
      // 저장 후 헤더 데이터 새로고침 (집계 필드 업데이트를 위해)
      const updatedHeaders = await getOrderHeaders(searchParams);
      setHeaderRows(updatedHeaders || []);
      
      // 현재 선택된 헤더 업데이트
      if (selectedHeader) {
        const updatedHeader = updatedHeaders?.find(h => h.orderNo === selectedHeader.orderNo);
        if (updatedHeader) {
          setSelectedHeader(updatedHeader);
        }
      }
      
      // 저장 후 상세 데이터 새로고침
      if (selectedHeader?.orderNo) {
        await loadDetails(selectedHeader.orderNo);
      }
      setEditedDetailRows(new Set());
      
      Message.showSuccess('상세 정보가 저장되었습니다.');
    } catch (error) {
      Message.showError(error);
    }
  };

  // 편집모드 토글
  const handleToggleEditMode = () => {
    if (!isEditMode) {
      // 편집모드 진입 시 거래처 목록 재조회 (신규 거래처 즉시 선택 가능하도록)
      loadVendors();
    } else {
      // 편집모드 종료 시 변경사항 초기화 여부 확인
      setEditedHeaderRows(new Set());
    }
    setIsEditMode(prev => !prev);
  };

  // 템플릿 다운로드 함수
  const handleTemplateDownload = async () => {
    if (templateInfo && templateInfo.columns && Array.isArray(templateInfo.columns)) {
      try {
        const success = downloadExcelTemplate(
          templateInfo.columns,
          templateInfo.templateName || 'ORDER_HEADER',
          templateInfo.sampleData || []
        );
        
        if (success) {
          Message.showSuccess('템플릿 다운로드가 완료되었습니다.');
        }
      } catch (error) {
        console.error('템플릿 다운로드 오류:', error);
        Message.showError('템플릿 다운로드 중 오류가 발생했습니다.');
      }
    } else {
      Message.showWarning('템플릿 정보를 불러올 수 없습니다.');
    }
  };

  // 엑셀 업로드 함수
  const handleExcelUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const response = await uploadExcel(file, 'ORDER_HEADER', null, {});
        
        if (response.success) {
          // 업로드 성공 후 헤더 데이터 새로고침
          await handleSearch(searchParams, true);
          Message.showSuccess(`업로드 완료: 총 ${response.totalRows}개 중 ${response.successRows}개 성공`);
        }
      } catch (error) {
        console.error('엑셀 업로드 오류:', error);
        Message.showError('엑셀 업로드 중 오류가 발생했습니다.');
      }
    };

    input.click();
  };

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* 검색 조건 영역 */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
        <SearchCondition
          title="주문 검색"
          onSearch={() => handleSearch(searchParams, true)}
          onReset={handleReset}
        >
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="주문번호"
              name="orderNo"
              value={orderNoInput}
              onChange={(e) => setOrderNoInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="주문일자 From"
              value={parseDate(searchParams.fromDate)}
              onChange={(newValue) => handleSearch({ ...searchParams, fromDate: formatDate(newValue) })}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="주문일자 To"
              value={parseDate(searchParams.toDate)}
              onChange={(newValue) => handleSearch({ ...searchParams, toDate: formatDate(newValue) })}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>고객사</InputLabel>
              <Select
                value={searchParams.customerId || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : e.target.value;
                  handleSearch({ ...searchParams, customerId: value });
                }}
                label="고객사"
              >
                <MenuItem value="">전체</MenuItem>
                {vendors.map(vendor => (
                  <MenuItem key={vendor.vendorId} value={vendor.vendorId}>
                    {vendor.vendorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>제품</InputLabel>
              <Select
                value={searchParams.materialId || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : e.target.value;
                  handleSearch({ ...searchParams, materialId: value });
                }}
                label="제품"
              >
                <MenuItem value="">전체</MenuItem>
                {products.map(product => (
                  <MenuItem key={product.systemMaterialId} value={product.systemMaterialId}>
                    {`${product.materialName} (${product.materialStandard})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </SearchCondition>
      </LocalizationProvider>

      {/* 기본 주문정보 영역 */}
      <Paper sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Box component="h3" sx={{ m: 0 }}>기본 주문정보</Box>
            <Stack direction="row" spacing={1}>
              {headerRows.some(row => row.id) ? (
                <Button 
                  startIcon={<EditIcon />} 
                  onClick={handleToggleEditMode}
                  color={isEditMode ? "primary" : "default"}
                >
                  {isEditMode ? '편집 중' : '편집'}
                </Button>
              ) : null}
              <Button startIcon={<AddIcon />} onClick={handleAddHeader}>
                등록
              </Button>
              <Button 
                startIcon={<SaveIcon />} 
                onClick={handleSaveHeader}
                disabled={!isEditMode && !headerRows.some(row => !row.id)}
              >
                저장
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => selectedHeader && handleDeleteHeader(selectedHeader.id)}
                color='error'
                disabled={!selectedHeader}
              >
                삭제
              </Button>
              <Button
                startIcon={<GetAppIcon />}
                onClick={() => Message.showInfo('준비 중입니다.')}
                color="secondary"
                variant="outlined"
              >
                템플릿 다운로드
              </Button>
              <Button
                startIcon={<CloudUploadIcon />}
                onClick={() => Message.showInfo('준비 중입니다.')}
                color="info"
                variant="outlined"
              >
                엑셀 업로드
              </Button>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <EnhancedDataGridWrapper
            rows={headerRows}
            columns={headerColumns}
            onRowClick={handleHeaderRowClick}
            loading={loading}
            hideToolbar
            gridProps={{
              editMode: 'cell',
              getRowId: (row) => row.orderNo,
              processRowUpdate: processHeaderRowUpdate,
              isCellEditable: isHeaderCellEditable,
              disableSelectionOnClick: true,
              autoHeight: false
            }}
          />
        </Box>
      </Paper>

      {/* 주문 상세정보 영역 */}
      <Collapse in={Boolean(selectedHeader)}>
        <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Box component="h3" sx={{ m: 0 }}>주문 상세정보</Box>
              <Stack direction="row" spacing={1}>
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleAddDetail}
                  disabled={!selectedHeader}
                >
                  등록
                </Button>
                <Button 
                  startIcon={<SaveIcon />} 
                  onClick={handleSaveDetail}
                  disabled={!selectedHeader}
                >
                  저장
                </Button>
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteDetail}
                  disabled={!selectedDetail}
                  color="error"
                >
                  삭제
                </Button>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <EnhancedDataGridWrapper
              rows={detailRows}
              columns={detailColumns}
              loading={loading}
              hideToolbar
              onRowClick={handleDetailRowClick}
              gridProps={{
                editMode: 'cell',
                getRowId: (row) => row.orderSubNo,
                processRowUpdate: processDetailRowUpdate,
                isCellEditable: isDetailCellEditable,
                disableSelectionOnClick: false,
                autoHeight: false,
                selectionModel: selectedDetail ? [selectedDetail.orderSubNo] : []
              }}
            />
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default OrderRegistration; 